import { Injectable } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { ModalService } from 'app/core-lib/dialogs/modal/modal.service';
import { TranslateObjectPipe } from 'app/shared/pipe';
import { CoreLibCreateCaseModalComponent } from 'app/core-lib/dialogs/create-case-modal/create-case-modal.component';
import { CasesService } from 'app/shared/cases';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { UtilService } from 'app/shared/util.service';
import { Store } from '@ngxs/store';
import { UsersState } from 'app/states/users/users.state';
import { CustomersState } from 'app/states/customers/customers.state';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { CoreLibLimitCasesModalComponent } from 'app/core-lib/dialogs/limit-cases/limit-cases.component';
import { ConfigCasesService } from 'app/cases/services/config-cases.service';
import { DefaultLanguageState } from 'app/states/default-language/default-language.state';
import { AlertService } from 'app/core-lib/services/alerts/alert.service';
import { CoreLibCasesAlertsComponent } from 'app/core-lib/alerts/cases-alerts/cases-alerts.component';
import { HotelService } from 'app/shared/hotel/hotel.service';

@Injectable({
  providedIn: 'root'
})
export class CreateCaseDialogService {

  readonly UPSELL_LINK = {
    'es': 'https://bit.ly/35og5e4',
    'pt': 'https://bit.ly/3sdopqh',
    'en': 'https://bit.ly/3h6pgCO',
  }
  readonly UPSELL_LINK_ADMIN = {
    'es': 'https://bit.ly/3h87izJ',
    'pt': 'https://bit.ly/3scXF9h',
    'en': 'https://bit.ly/3IhkBtw',
  };
  readonly CLASSIFICATION_LANGS = {
    'es': 'clasification_text',
    'en': 'clasification_text_en',
    'pt': 'clasification_text_pt'
  }

  public onSuccessCreatedCase$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  monthlyLimit = 0;

  constructor(
    private modalService: ModalService,
    private casesService: CasesService,
    private translateObjectPipe: TranslateObjectPipe,
    private utilService: UtilService,
    private store: Store,
    private snackbar: MatSnackBar,
    private translate: TranslateService,
    private configCasesService: ConfigCasesService,
    private titleCasePipe: TitleCasePipe,
    private alertService: AlertService,
    private hotelService: HotelService,
  ) { }

  // assigned = 'review', 'nps', 'prestay'
  // item = object according to product
  openModal(currentUser, associated = null, item = null, product = null, detectedCase = null) {
    this.fetchData(currentUser, associated, item, product, detectedCase)
  }

  fetchData(currentUser, associated, item, product, detectedCase) {
    const currentCustomerId = this.store.selectSnapshot((state) => state.customers.currentCustomer?.id);
    forkJoin({
      areas: this.hotelService.getAreas(currentCustomerId),
      baseCases: this.fetchBaseCases(),
      onsiteGuests: this.fetchOnsiteGuests(currentCustomerId),
    }).subscribe(({ areas, baseCases, onsiteGuests }: { areas: any; baseCases: any; onsiteGuests: any }) => {
      const config = {
        data: {
          noCloseOnBackdropClick: true,
          currentUser,
          types: baseCases.case_types,
          priorities: baseCases.priorities,
          productItemType: baseCases.product_item_type,
          states: baseCases.states,
          users: this.store.selectSnapshot(UsersState.currentUsers),
          translatesPipe: {
            types: this.displayFnTypes,
            priorities: this.displayFnPriorities,
            chips: this.displayChipsFn,
          },
          areas: this.transformAreas(areas) || [],
          associated,
          item,
          product,
          onsiteGuests,
          detectedCase,
          reasonsDiscarded: baseCases.reasons_discarded,
        },
      };
      this.displayModal(config);
    });
    this.configCasesService.getCasesLimit(currentCustomerId).subscribe((result) => {
      if (result) {
        this.monthlyLimit = result?.monthly_limit || 0;
      }
    });
  }

  displayModal(config) {
    const classes = [
      'overlay-panel',
      '!tw-h-auto',
      'no-padding',
      '!tw-overflow-auto',
    ];

    classes.push(config.data?.item?.answer?.detectedCase || config.data?.item?.detected_cases ? 'lg' : 'md');

    const dialog = this.modalService.open(CoreLibCreateCaseModalComponent, config, classes)
    // El modal hace el POST y solo cierra si sale bien, así que acá únicamente se reacciona al
    // resultado. Los errores recuperables los muestra el propio modal, que se queda abierto con
    // el formulario intacto.
    dialog.afterClosed().subscribe((result: any) => {
      if (!result || result === 'discard') return;

      if (result.limitError) {
        this.limitCasesError(config.data.currentUser);
        return;
      }

      if (result.createdCase) {
        this.showNotification(
          {
            translateKey: 'cod',
            translateValue: result.createdCase.public_id,
            url: result.createdCase.url_to_share,
            type: 'success',
          },
          CoreLibCasesAlertsComponent,
        );
        this.onSuccessCreatedCase$.next(true);
      }
    })
  }

  showNotification(data, component = null) {
    this.alertService.handleAlert(
      'cases.commons.success_create',
      9000,
      'bottom',
      'end',
      'snackbar-panel-success-user-config',
      component ? component : null,
      data ? data : null,
    );
  }

  openSnackBar(snackbarOptions, message) {
    this.translate.get(message)
      .subscribe(text => {
        this.snackbar.open(text, 'x', snackbarOptions);
      });
  }

  limitCasesError(currentUser) {
    const config = {
      data: {
        user: currentUser,
        upsellLink: this.getUpsellLink(currentUser.superAdmin, currentUser.admin),
        monthlyLimit: this.monthlyLimit,
        hotel: this.store.selectSnapshot(CustomersState.currentCustomer)
      }
    }
    const classes = [
      'overlay-panel',
      'no-padding',
      '!tw-h-fit'
    ]
    this.modalService.open(CoreLibLimitCasesModalComponent, config, classes)
  }


  fetchAreas(customerId: number, languageId: number) {
    return this.casesService.getAreas(customerId, languageId);
  }

  fetchBaseCases() {
    return this.casesService.getBase();
  }

  fetchOnsiteGuests(customerId: number) {
    return this.casesService.getOnsiteGuests(customerId);
  }

  displayFnTypes = (option) => {
    return this.translateObjectPipe.transform(option.translates, 'name')
  }

  displayFnPriorities = (option) => {
    return this.titleCasePipe.transform(
      this.translateObjectPipe.transform(option.translates, 'name')
    )
  }

  displayChipsFn = (option) => {
    if ( this.utilService.currentLanguage === 'es' )
      return option.clasification_text;
    if ( this.utilService.currentLanguage === 'en' )
      return option.clasification_text_en;
    if ( this.utilService.currentLanguage === 'pt' )
      return option.clasification_text_pt;
  }

  mapAreas(areas_translates, areas) {
    return areas_translates.reduce((prev, curr) => {
      const considerArea = areas.find(area => area.id === curr.area_id);
      if (!considerArea) {
        return [
          ...prev
        ]
      }
      const existArea = prev.findIndex(area => area.id === curr.area_id);
      const key = this.CLASSIFICATION_LANGS[curr?.language?.code];
      if ( existArea >= 0 ) {
        prev[existArea][key] = curr.name;
        return [
          ...prev
        ]

      } else {
        return [
          ...prev,
          {
            id: curr.area_id,
            [key]: curr.name
          }
        ];
      }
    }, []);
  }

  getUpsellLink(userSuperAdmin, userAdmin) {
    const currentLang = this.utilService.getCurrentLanguage()
    return `${userSuperAdmin || userAdmin ? this.UPSELL_LINK_ADMIN[currentLang] : this.UPSELL_LINK[currentLang]}`;
  }

  isCaseCreated() {
    return this.onSuccessCreatedCase$.asObservable();
  }

  transformAreas(areasFromApi: any[]) {
    const currentLanguage = this.store.selectSnapshot((state) => state.defaultLanguage.code);
    return areasFromApi.reduce((acc, area) => {
      if (area.active) {
        const translate = area.translates.find((t) => t.language.code === currentLanguage);
        acc.push({
          id: area.area_id,
          name: translate ? translate.name : area.translates[0]?.name || '',
        });
      }
      return acc;
    }, []);
  }
}
